import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { ScreenTitle } from "@/components/ScreenTitle";
import {
  ChevronRight, RotateCcw, PlugZap, LogOut, Save, X, Plus, KeyRound, UserPlus, Trash2,
} from "lucide-react";
import { setSession } from "@/lib/api";

export default function SettingsPage() {
  const { logout, email } = useAuth();
  const [cfg, setCfg] = useState(null);
  const [form, setForm] = useState({});
  const [open, setOpen] = useState("llm");
  const [wpUsers, setWpUsers] = useState(null);
  const [loadingUsers, setLoadingUsers] = useState(false);

  const load = async () => {
    const r = await api.get("/config");
    setCfg(r.data);
    setForm({});
  };
  useEffect(() => { load(); }, []);

  const loadWpUsers = async () => {
    if (wpUsers) return; // cached
    setLoadingUsers(true);
    try {
      const r = await api.get("/config/wp-users");
      setWpUsers(r.data.users || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impossible de récupérer les utilisateurs WP");
      setWpUsers([]);
    } finally {
      setLoadingUsers(false);
    }
  };

  const save = async (patch) => {
    try {
      const r = await api.put("/config", patch);
      setCfg(r.data);
      setForm({});
      toast.success("Enregistré");
    } catch (e) {
      toast.error("Erreur d'enregistrement");
    }
  };
  const val = (k) => (k in form ? form[k] : cfg?.[k] ?? "");
  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  if (!cfg) {
    return <div className="p-10 font-mono text-sm text-[var(--text-muted)]">Chargement…</div>;
  }

  return (
    <div data-testid="settings-page">
      <ScreenTitle eyebrow="Paramètres" title="Réglages" />

      <div className="border-t border-[var(--border-default)]">
        <Section
          id="llm"
          open={open}
          setOpen={setOpen}
          title="Clés LLM"
          subtitle={`${cfg.llm_provider} · ${cfg.llm_model}`}
        >
          <Field label="Provider">
            <select
              value={val("llm_provider")}
              onChange={(e) => set("llm_provider", e.target.value)}
              data-testid="llm-provider-select"
              className="w-full min-h-[44px] border border-[var(--border-default)] px-3 font-mono text-sm rounded-none bg-white focus:outline-none focus:border-black"
            >
              <option value="anthropic">Anthropic (clé utilisateur)</option>
              <option value="emergent">Emergent Universal (secours)</option>
            </select>
          </Field>
          <Field label="Modèle">
            <input
              value={val("llm_model")}
              onChange={(e) => set("llm_model", e.target.value)}
              data-testid="llm-model-input"
              className="w-full min-h-[44px] border border-[var(--border-default)] px-3 font-mono text-sm rounded-none focus:outline-none focus:border-black"
            />
          </Field>
          <Field label={`Clé Anthropic ${cfg.anthropic_api_key_set ? "· ✔ définie" : "· non renseignée"}`}>
            <input
              type="password"
              value={form.anthropic_api_key ?? ""}
              onChange={(e) => set("anthropic_api_key", e.target.value)}
              placeholder={cfg.anthropic_api_key_set ? "•••••• (laisser vide pour conserver)" : "sk-ant-…"}
              data-testid="anthropic-key-input"
              className="w-full min-h-[44px] border border-[var(--border-default)] px-3 font-mono text-sm rounded-none focus:outline-none focus:border-black"
            />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Température">
              <input
                type="number" min="0" max="1" step="0.05"
                value={val("llm_temperature")}
                onChange={(e) => set("llm_temperature", parseFloat(e.target.value))}
                data-testid="llm-temp-input"
                className="w-full min-h-[44px] border border-[var(--border-default)] px-3 font-mono text-sm rounded-none focus:outline-none focus:border-black"
              />
            </Field>
            <Field label="Max tokens">
              <input
                type="number" min="500" max="16000" step="100"
                value={val("llm_max_tokens")}
                onChange={(e) => set("llm_max_tokens", parseInt(e.target.value, 10))}
                data-testid="llm-maxtok-input"
                className="w-full min-h-[44px] border border-[var(--border-default)] px-3 font-mono text-sm rounded-none focus:outline-none focus:border-black"
              />
            </Field>
          </div>
          <SaveBtn
            testid="save-llm-btn"
            onClick={() => save({
              llm_provider: val("llm_provider"),
              llm_model: val("llm_model"),
              llm_temperature: val("llm_temperature"),
              llm_max_tokens: val("llm_max_tokens"),
              ...(form.anthropic_api_key !== undefined && { anthropic_api_key: form.anthropic_api_key }),
            })}
          />
        </Section>

        <Section
          id="wp"
          open={open}
          setOpen={setOpen}
          title="WordPress"
          subtitle={cfg.wp_site_url || "non configuré"}
        >
          <div className="text-[11px] font-mono text-[var(--text-secondary)] bg-[var(--bg-sidebar)] border-l-2 border-black px-3 py-2">
            La publication automatique par le scheduler est verrouillée sur « brouillon ».
            Seul le bouton « Publier » de la File d&apos;attente pousse un article en ligne.
          </div>
          <Field label="URL du site">
            <input
              type="url"
              value={val("wp_site_url")}
              onChange={(e) => set("wp_site_url", e.target.value)}
              placeholder="https://mon-media.fr"
              data-testid="wp-url-input"
              className="w-full min-h-[44px] border border-[var(--border-default)] px-3 font-mono text-sm rounded-none focus:outline-none focus:border-black"
            />
          </Field>
          <Field label="Identifiant">
            <input
              value={val("wp_username")}
              onChange={(e) => set("wp_username", e.target.value)}
              data-testid="wp-user-input"
              className="w-full min-h-[44px] border border-[var(--border-default)] px-3 font-mono text-sm rounded-none focus:outline-none focus:border-black"
            />
          </Field>
          <Field label={`Application Password ${cfg.wp_app_password_set ? "· ✔ défini" : "· non renseigné"}`}>
            <input
              type="password"
              value={form.wp_app_password ?? ""}
              onChange={(e) => set("wp_app_password", e.target.value)}
              placeholder={cfg.wp_app_password_set ? "•••••• (laisser vide pour conserver)" : "xxxx xxxx xxxx xxxx"}
              data-testid="wp-pass-input"
              className="w-full min-h-[44px] border border-[var(--border-default)] px-3 font-mono text-sm rounded-none focus:outline-none focus:border-black"
            />
          </Field>
          <Field
            label={`Auteur par défaut des brouillons${
              cfg.wp_default_author_id ? ` · id=${cfg.wp_default_author_id}` : " · non défini"
            }`}
            hint="Attribué automatiquement à tout nouveau brouillon poussé vers WP. Nécessite que l'utilisateur existe côté WordPress."
          >
            <div className="flex items-center gap-2">
              <select
                value={val("wp_default_author_id") ?? ""}
                onChange={(e) => set("wp_default_author_id",
                  e.target.value ? parseInt(e.target.value, 10) : null)}
                onFocus={loadWpUsers}
                data-testid="wp-author-select"
                className="flex-1 min-h-[44px] border border-[var(--border-default)] px-3 font-mono text-sm rounded-none bg-white focus:outline-none focus:border-black"
              >
                <option value="">
                  {loadingUsers ? "Chargement…" : "— Auteur du compte API par défaut —"}
                </option>
                {(wpUsers || []).map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name} (#{u.id})
                  </option>
                ))}
                {!wpUsers && cfg.wp_default_author_id && (
                  <option value={cfg.wp_default_author_id}>
                    (actuel) id #{cfg.wp_default_author_id}
                  </option>
                )}
              </select>
              <button
                type="button"
                onClick={() => { setWpUsers(null); loadWpUsers(); }}
                disabled={loadingUsers}
                data-testid="wp-author-reload"
                className="min-h-[44px] min-w-[44px] flex items-center justify-center border border-[var(--border-default)] hover:border-black disabled:opacity-50"
                aria-label="Recharger la liste des utilisateurs"
                title="Recharger depuis WordPress"
              >
                <RotateCcw className="w-3.5 h-3.5" />
              </button>
            </div>
          </Field>
          <div className="flex flex-wrap gap-2">
            <SaveBtn
              testid="save-wp-btn"
              onClick={() => save({
                wp_site_url: val("wp_site_url"),
                wp_username: val("wp_username"),
                wp_default_author_id: val("wp_default_author_id") ?? null,
                ...(form.wp_app_password !== undefined && { wp_app_password: form.wp_app_password }),
              })}
            />
            <button
              onClick={async () => {
                try {
                  const r = await api.post("/config/test-wordpress");
                  toast.success(`Connecté en tant que ${r.data.user}`);
                } catch (e) {
                  toast.error(e?.response?.data?.detail || "Connexion échouée");
                }
              }}
              data-testid="test-wp-btn"
              className="min-h-[44px] px-4 border border-black text-xs uppercase tracking-widest font-mono hover:bg-black hover:text-white transition-colors flex items-center gap-2"
            >
              <PlugZap className="w-3.5 h-3.5" /> Tester
            </button>
          </div>
        </Section>

        <Section
          id="telegram"
          open={open}
          setOpen={setOpen}
          title="Telegram"
          subtitle={cfg.telegram_notify_on_draft ? "notifications actives" : "désactivé"}
        >
          <Field label={`Bot token ${cfg.telegram_bot_token_set ? "· ✔ défini" : "· non renseigné"}`}>
            <input
              type="password"
              value={form.telegram_bot_token ?? ""}
              onChange={(e) => set("telegram_bot_token", e.target.value)}
              placeholder={cfg.telegram_bot_token_set ? "•••••• (laisser vide pour conserver)" : "123456:AA…"}
              data-testid="tg-token-input"
              className="w-full min-h-[44px] border border-[var(--border-default)] px-3 font-mono text-sm rounded-none focus:outline-none focus:border-black"
            />
          </Field>
          <Field label="Chat ID">
            <input
              value={val("telegram_chat_id")}
              onChange={(e) => set("telegram_chat_id", e.target.value)}
              placeholder="-100…"
              data-testid="tg-chat-input"
              className="w-full min-h-[44px] border border-[var(--border-default)] px-3 font-mono text-sm rounded-none focus:outline-none focus:border-black"
            />
          </Field>
          <label className="flex items-center gap-3 min-h-[44px] text-sm">
            <input
              type="checkbox"
              checked={!!val("telegram_notify_on_draft")}
              onChange={(e) => set("telegram_notify_on_draft", e.target.checked)}
              data-testid="tg-notify"
              className="w-5 h-5 accent-black"
            />
            Notifier à chaque nouveau brouillon
          </label>
          <div className="flex flex-wrap gap-2">
            <SaveBtn
              testid="save-tg-btn"
              onClick={() => save({
                telegram_chat_id: val("telegram_chat_id"),
                telegram_notify_on_draft: !!val("telegram_notify_on_draft"),
                ...(form.telegram_bot_token !== undefined && { telegram_bot_token: form.telegram_bot_token }),
              })}
            />
            <button
              onClick={async () => {
                try {
                  await api.post("/config/test-telegram");
                  toast.success("Message Telegram envoyé");
                } catch (e) {
                  toast.error(e?.response?.data?.detail || "Envoi échoué");
                }
              }}
              data-testid="test-tg-btn"
              className="min-h-[44px] px-4 border border-black text-xs uppercase tracking-widest font-mono hover:bg-black hover:text-white transition-colors flex items-center gap-2"
            >
              <PlugZap className="w-3.5 h-3.5" /> Tester
            </button>
          </div>
        </Section>

        <Section
          id="images"
          open={open}
          setOpen={setOpen}
          title="Image de couverture"
          subtitle={
            cfg.image_provider === "none"
              ? "désactivée"
              : `${cfg.image_provider} · ${
                  cfg.image_provider === "pexels"
                    ? cfg.pexels_api_key_set ? "clé ✔" : "clé manquante"
                    : cfg.unsplash_access_key_set ? "clé ✔" : "clé manquante"
                }`
          }
        >
          <div className="text-[11px] font-mono text-[var(--text-secondary)] bg-[var(--bg-sidebar)] border-l-2 border-[var(--accent)] px-3 py-2">
            Fournisseurs légaux uniquement (Pexels, Unsplash). Aucune image n&apos;est scrappée
            depuis Google/Bing. L&apos;image est téléversée dans la médiathèque WordPress puis
            attachée en « featured media ». Titre, ALT et légende sont renseignés avec le titre
            de l&apos;article.
          </div>
          <Field label="Fournisseur">
            <select
              value={val("image_provider")}
              onChange={(e) => set("image_provider", e.target.value)}
              data-testid="image-provider-select"
              className="w-full min-h-[44px] border border-[var(--border-default)] px-3 font-mono text-sm rounded-none bg-white focus:outline-none focus:border-black"
            >
              <option value="none">Aucun (pas d&apos;image auto)</option>
              <option value="pexels">Pexels</option>
              <option value="unsplash">Unsplash</option>
            </select>
          </Field>
          <Field label={`Clé Pexels ${cfg.pexels_api_key_set ? "· ✔ définie" : "· non renseignée"}`}
                 hint="pexels.com/api — clé gratuite, requêtes /h généreuses.">
            <input
              type="password"
              value={form.pexels_api_key ?? ""}
              onChange={(e) => set("pexels_api_key", e.target.value)}
              placeholder={cfg.pexels_api_key_set ? "•••••• (laisser vide pour conserver)" : "563492…"}
              data-testid="pexels-key-input"
              className="w-full min-h-[44px] border border-[var(--border-default)] px-3 font-mono text-sm rounded-none focus:outline-none focus:border-black"
            />
          </Field>
          <Field label={`Access Key Unsplash ${cfg.unsplash_access_key_set ? "· ✔ définie" : "· non renseignée"}`}
                 hint="unsplash.com/developers — Access Key (pas le Secret Key).">
            <input
              type="password"
              value={form.unsplash_access_key ?? ""}
              onChange={(e) => set("unsplash_access_key", e.target.value)}
              placeholder={cfg.unsplash_access_key_set ? "•••••• (laisser vide pour conserver)" : "ak-…"}
              data-testid="unsplash-key-input"
              className="w-full min-h-[44px] border border-[var(--border-default)] px-3 font-mono text-sm rounded-none focus:outline-none focus:border-black"
            />
          </Field>
          <SaveBtn
            testid="save-images-btn"
            onClick={() => save({
              image_provider: val("image_provider"),
              ...(form.pexels_api_key !== undefined && { pexels_api_key: form.pexels_api_key }),
              ...(form.unsplash_access_key !== undefined && { unsplash_access_key: form.unsplash_access_key }),
            })}
          />
        </Section>

        <Section
          id="schedule"
          open={open}
          setOpen={setOpen}
          title="Cadence horaire"
          subtitle={`haute ${cfg.high_start_hour}h→${cfg.high_end_hour}h · ${cfg.high_quota_per_hour}/h · basse ${cfg.low_quota_per_hour}/h`}
        >
          <label className="flex items-center gap-3 min-h-[44px] text-sm">
            <input
              type="checkbox"
              checked={!!val("scheduler_enabled")}
              onChange={(e) => set("scheduler_enabled", e.target.checked)}
              data-testid="sched-enabled"
              className="w-5 h-5 accent-black"
            />
            Scheduler activé
          </label>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Plage haute · début">
              <input
                type="number" min="0" max="23"
                value={val("high_start_hour")}
                onChange={(e) => set("high_start_hour", parseInt(e.target.value, 10))}
                data-testid="high-start"
                className="w-full min-h-[44px] border border-[var(--border-default)] px-3 font-mono text-sm rounded-none focus:outline-none focus:border-black"
              />
            </Field>
            <Field label="Plage haute · fin">
              <input
                type="number" min="0" max="24"
                value={val("high_end_hour")}
                onChange={(e) => set("high_end_hour", parseInt(e.target.value, 10))}
                data-testid="high-end"
                className="w-full min-h-[44px] border border-[var(--border-default)] px-3 font-mono text-sm rounded-none focus:outline-none focus:border-black"
              />
            </Field>
            <Field label="Quota · haute (/h)">
              <input
                type="number" min="0" max="20"
                value={val("high_quota_per_hour")}
                onChange={(e) => set("high_quota_per_hour", parseInt(e.target.value, 10))}
                data-testid="high-quota"
                className="w-full min-h-[44px] border border-[var(--border-default)] px-3 font-mono text-sm rounded-none focus:outline-none focus:border-black"
              />
            </Field>
            <Field label="Quota · basse (/h)">
              <input
                type="number" min="0" max="20"
                value={val("low_quota_per_hour")}
                onChange={(e) => set("low_quota_per_hour", parseInt(e.target.value, 10))}
                data-testid="low-quota"
                className="w-full min-h-[44px] border border-[var(--border-default)] px-3 font-mono text-sm rounded-none focus:outline-none focus:border-black"
              />
            </Field>
          </div>
          <SaveBtn
            testid="save-schedule-btn"
            onClick={() => save({
              scheduler_enabled: !!val("scheduler_enabled"),
              high_start_hour: val("high_start_hour"),
              high_end_hour: val("high_end_hour"),
              high_quota_per_hour: val("high_quota_per_hour"),
              low_quota_per_hour: val("low_quota_per_hour"),
            })}
          />
        </Section>

        <Section
          id="freshness"
          open={open}
          setOpen={setOpen}
          title="Fraîcheur des articles"
          subtitle={`ignorer > ${cfg.max_item_age_hours}h`}
        >
          <Field label="Ne pas traiter les items RSS plus vieux que">
            <select
              value={val("max_item_age_hours")}
              onChange={(e) => set("max_item_age_hours", parseInt(e.target.value, 10))}
              data-testid="max-age-select"
              className="w-full min-h-[44px] border border-[var(--border-default)] px-3 font-mono text-sm rounded-none bg-white focus:outline-none focus:border-black"
            >
              <option value={6}>6 heures</option>
              <option value={12}>12 heures</option>
              <option value={24}>24 heures (défaut)</option>
              <option value={48}>48 heures</option>
              <option value={72}>72 heures</option>
            </select>
          </Field>
          <p className="text-xs text-[var(--text-muted)] leading-relaxed">
            Les items RSS publiés au-delà de ce délai sont ignorés (marqués comme traités
            sans être poussés). Les articles commandés manuellement par URL ne sont
            <strong> pas concernés</strong> par ce filtre.
          </p>
          <SaveBtn
            testid="save-freshness-btn"
            onClick={() => save({ max_item_age_hours: val("max_item_age_hours") })}
          />
        </Section>

        <Section
          id="retention"
          open={open}
          setOpen={setOpen}
          title="Rétention des logs"
          subtitle={`${cfg.log_retention_hours} heures`}
        >
          <Field label="Durée de conservation">
            <select
              value={val("log_retention_hours")}
              onChange={(e) => set("log_retention_hours", parseInt(e.target.value, 10))}
              data-testid="retention-select"
              className="w-full min-h-[44px] border border-[var(--border-default)] px-3 font-mono text-sm rounded-none bg-white focus:outline-none focus:border-black"
            >
              <option value={24}>24 heures</option>
              <option value={48}>48 heures (défaut)</option>
              <option value={72}>72 heures</option>
              <option value={168}>7 jours</option>
              <option value={336}>14 jours</option>
            </select>
          </Field>
          <SaveBtn
            testid="save-retention-btn"
            onClick={() => save({ log_retention_hours: val("log_retention_hours") })}
          />
        </Section>

        <Section
          id="risk"
          open={open}
          setOpen={setOpen}
          title="Score de risque IA"
          subtitle={`orange ≥ ${cfg.risk_orange_threshold} · rouge ≥ ${cfg.risk_red_threshold}`}
        >
          <div className="text-[11px] font-mono text-[var(--text-secondary)] bg-[var(--bg-sidebar)] border-l-2 border-[var(--accent)] px-3 py-2">
            Le score compte les occurrences de tes tournures interdites dans la sortie du LLM.
            Ajoute ou retire des entrées : elles remontent automatiquement dans le comptage et le surlignage du détail.
          </div>

          <BlacklistEditor
            label="Connecteurs interdits"
            testidPrefix="bl-connectors"
            items={val("risk_connectors") || []}
            onChange={(v) => set("risk_connectors", v)}
          />
          <BlacklistEditor
            label="Vocabulaire/clichés"
            testidPrefix="bl-vocabulary"
            items={val("risk_vocabulary") || []}
            onChange={(v) => set("risk_vocabulary", v)}
          />
          <BlacklistEditor
            label="Typographie interdite"
            hint="Un caractère par entrée (— tiret cadratin, – demi-cadratin, etc.)"
            testidPrefix="bl-typography"
            items={val("risk_typography") || []}
            onChange={(v) => set("risk_typography", v)}
          />
          <div className="grid grid-cols-2 gap-3">
            <Field label="Seuil orange">
              <input
                type="number" min="1" max="20"
                value={val("risk_orange_threshold")}
                onChange={(e) => set("risk_orange_threshold", parseInt(e.target.value, 10))}
                data-testid="risk-orange"
                className="w-full min-h-[44px] border border-[var(--border-default)] px-3 font-mono text-sm rounded-none focus:outline-none focus:border-black"
              />
            </Field>
            <Field label="Seuil rouge">
              <input
                type="number" min="1" max="30"
                value={val("risk_red_threshold")}
                onChange={(e) => set("risk_red_threshold", parseInt(e.target.value, 10))}
                data-testid="risk-red"
                className="w-full min-h-[44px] border border-[var(--border-default)] px-3 font-mono text-sm rounded-none focus:outline-none focus:border-black"
              />
            </Field>
          </div>
          <SaveBtn
            testid="save-risk-btn"
            onClick={() => save({
              risk_connectors: val("risk_connectors"),
              risk_vocabulary: val("risk_vocabulary"),
              risk_typography: val("risk_typography"),
              risk_orange_threshold: val("risk_orange_threshold"),
              risk_red_threshold: val("risk_red_threshold"),
            })}
          />
        </Section>

        <Section
          id="prompt"
          open={open}
          setOpen={setOpen}
          title="Prompt de réécriture"
          subtitle={`${cfg.prompt_template.length} caractères`}
        >
          <textarea
            value={val("prompt_template")}
            onChange={(e) => set("prompt_template", e.target.value)}
            data-testid="prompt-textarea"
            className="w-full h-[480px] font-mono text-[12px] leading-relaxed bg-[var(--bg-sidebar)] border border-[var(--border-default)] rounded-none p-3 focus:outline-none focus:border-black"
            spellCheck={false}
          />
          <div className="flex flex-wrap gap-2">
            <SaveBtn
              testid="save-prompt-btn"
              onClick={() => save({ prompt_template: val("prompt_template") })}
            />
            <button
              onClick={async () => {
                if (!window.confirm("Restaurer le prompt par défaut ?")) return;
                const r = await api.post("/config/reset-prompt");
                setCfg(r.data);
                setForm({});
                toast.success("Prompt réinitialisé");
              }}
              data-testid="reset-prompt-btn"
              className="min-h-[44px] px-4 border border-[var(--border-default)] text-xs uppercase tracking-widest font-mono hover:border-black transition-colors flex items-center gap-2"
            >
              <RotateCcw className="w-3.5 h-3.5" /> Réinitialiser
            </button>
          </div>
        </Section>
        <Section
          id="users"
          open={open}
          setOpen={setOpen}
          title="Utilisateurs"
          subtitle="créer, changer les mots de passe, supprimer"
        >
          <UsersAdmin currentEmail={email} />
        </Section>

      </div>

      {/* Account */}
      <div className="px-5 py-6 border-t border-[var(--border-default)] flex items-center justify-between">
        <div className="text-[11px] font-mono text-[var(--text-muted)] truncate">
          Session : {email}
        </div>
        <button
          onClick={logout}
          data-testid="logout-button"
          className="min-h-[44px] px-4 border border-black text-xs uppercase tracking-widest font-mono hover:bg-black hover:text-white transition-colors flex items-center gap-2"
        >
          <LogOut className="w-3.5 h-3.5" /> Déconnexion
        </button>
      </div>
    </div>
  );
}

// --------- Building blocks ---------

function Section({ id, title, subtitle, children, open, setOpen }) {
  const isOpen = open === id;
  return (
    <div className="border-b border-[var(--border-default)]" data-testid={`section-${id}`}>
      <button
        onClick={() => setOpen(isOpen ? null : id)}
        aria-expanded={isOpen}
        data-testid={`section-toggle-${id}`}
        className={`w-full min-h-[64px] px-5 py-3 flex items-center gap-3 text-left ${isOpen ? "bg-white" : "hover:bg-white"}`}
      >
        <ChevronRight
          className={`w-4 h-4 shrink-0 transition-transform ${isOpen ? "rotate-90" : ""}`}
          strokeWidth={1.75}
        />
        <div className="min-w-0 flex-1">
          <div className="font-headings text-lg leading-tight">{title}</div>
          {subtitle && (
            <div className="text-[11px] font-mono text-[var(--text-muted)] truncate mt-0.5">
              {subtitle}
            </div>
          )}
        </div>
      </button>
      {isOpen && (
        <div className="px-5 pb-5 space-y-3 bg-white">{children}</div>
      )}
    </div>
  );
}

function Field({ label, children, hint }) {
  return (
    <div>
      <label className="caption block mb-1">{label}</label>
      {children}
      {hint && <p className="text-[11px] font-mono text-[var(--text-muted)] mt-1">{hint}</p>}
    </div>
  );
}

function SaveBtn({ onClick, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className="min-h-[44px] px-4 bg-black text-white text-xs uppercase tracking-widest font-medium hover:bg-[#222] transition-colors flex items-center gap-2"
    >
      <Save className="w-3.5 h-3.5" /> Enregistrer
    </button>
  );
}

function BlacklistEditor({ label, items, onChange, testidPrefix, hint }) {
  const [draft, setDraft] = useState("");
  const add = () => {
    const v = draft.trim();
    if (!v) return;
    if (items.includes(v)) { setDraft(""); return; }
    onChange([...items, v]);
    setDraft("");
  };
  const removeAt = (i) => {
    const next = items.slice();
    next.splice(i, 1);
    onChange(next);
  };
  return (
    <Field label={label} hint={hint}>
      <div className="flex flex-wrap gap-1.5 mb-2" data-testid={`${testidPrefix}-list`}>
        {items.length === 0 && (
          <span className="text-[11px] font-mono text-[var(--text-muted)]">Aucune entrée.</span>
        )}
        {items.map((it, i) => (
          <span
            key={i}
            data-testid={`${testidPrefix}-item-${i}`}
            className="inline-flex items-center gap-1 px-2 py-1 bg-[var(--accent-soft)] text-[var(--accent)] text-[12px] font-mono"
          >
            {it}
            <button
              onClick={() => removeAt(i)}
              data-testid={`${testidPrefix}-remove-${i}`}
              aria-label="Retirer"
              className="ml-0.5 hover:text-[var(--accent-strong)]"
            >
              <X className="w-3 h-3" strokeWidth={2} />
            </button>
          </span>
        ))}
      </div>
      <div className="flex gap-2">
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
          data-testid={`${testidPrefix}-input`}
          placeholder="Ajouter…"
          className="flex-1 min-h-[44px] border border-[var(--border-default)] px-3 font-mono text-sm rounded-none focus:outline-none focus:border-black"
        />
        <button
          onClick={add}
          data-testid={`${testidPrefix}-add`}
          className="min-h-[44px] min-w-[44px] flex items-center justify-center border border-black hover:bg-black hover:text-white transition-colors"
          aria-label="Ajouter"
        >
          <Plus className="w-4 h-4" />
        </button>
      </div>
    </Field>
  );
}

function UsersAdmin({ currentEmail }) {
  const [users, setUsers] = useState(null);
  const [newEmail, setNewEmail] = useState("");
  const [newPw, setNewPw] = useState("");
  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editPw, setEditPw] = useState("");
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    try {
      const r = await api.get("/users");
      setUsers(r.data);
    } catch (e) {
      toast.error("Impossible de charger les utilisateurs");
    }
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    if (!newEmail || !newPw) return;
    if (newPw.length < 8) {
      toast.error("Le mot de passe doit contenir au moins 8 caractères");
      return;
    }
    setCreating(true);
    try {
      await api.post("/users", { email: newEmail.trim().toLowerCase(), password: newPw });
      toast.success(`Utilisateur créé : ${newEmail}`);
      setNewEmail(""); setNewPw("");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Création impossible");
    } finally { setCreating(false); }
  };

  const savePassword = async (u) => {
    if (editPw.length < 8) {
      toast.error("Le mot de passe doit contenir au moins 8 caractères");
      return;
    }
    setBusyId(u.id);
    try {
      await api.post(`/users/${u.id}/password`, { new_password: editPw });
      toast.success(`Mot de passe mis à jour pour ${u.email}`);
      setEditingId(null); setEditPw("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Échec de la mise à jour");
    } finally { setBusyId(null); }
  };

  const remove = async (u) => {
    if (u.email.toLowerCase() === currentEmail.toLowerCase()) {
      toast.error("Impossible de supprimer votre propre compte");
      return;
    }
    if (!window.confirm(`Supprimer définitivement l'utilisateur « ${u.email} » ?`)) return;
    setBusyId(u.id);
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("Utilisateur supprimé");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Suppression impossible");
    } finally { setBusyId(null); }
  };

  return (
    <div data-testid="users-admin">
      {/* Create form */}
      <form onSubmit={create} className="border border-[var(--border-default)] p-3 space-y-2 bg-[var(--bg-sidebar)]">
        <div className="text-[11px] font-mono uppercase tracking-wider text-[var(--text-secondary)] flex items-center gap-1.5">
          <UserPlus className="w-3.5 h-3.5" /> Créer un utilisateur
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <input
            type="email"
            required
            placeholder="email@aimka.fr"
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            data-testid="user-new-email"
            className="min-h-[44px] border border-[var(--border-default)] px-3 font-mono text-sm rounded-none bg-white focus:outline-none focus:border-black"
          />
          <input
            type="password"
            required
            minLength={8}
            placeholder="mot de passe (8+ caractères)"
            value={newPw}
            onChange={(e) => setNewPw(e.target.value)}
            data-testid="user-new-password"
            className="min-h-[44px] border border-[var(--border-default)] px-3 font-mono text-sm rounded-none bg-white focus:outline-none focus:border-black"
          />
        </div>
        <button
          type="submit"
          disabled={creating}
          data-testid="user-create-submit"
          className="w-full min-h-[44px] bg-black text-white text-xs uppercase tracking-widest font-medium hover:bg-[#222] transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
        >
          <UserPlus className="w-3.5 h-3.5" />
          {creating ? "Création…" : "Créer l'utilisateur"}
        </button>
      </form>

      {/* Users list */}
      <div className="mt-3 border border-[var(--border-default)] bg-white" data-testid="users-list">
        {!users && (
          <div className="p-4 text-[12px] font-mono text-[var(--text-muted)]">Chargement…</div>
        )}
        {users && users.length === 0 && (
          <div className="p-4 text-[12px] font-mono text-[var(--text-muted)]">Aucun utilisateur.</div>
        )}
        {users && users.map((u) => {
          const isSelf = u.email.toLowerCase() === (currentEmail || "").toLowerCase();
          const editing = editingId === u.id;
          return (
            <div
              key={u.id}
              className="px-3 py-3 border-b border-[var(--border-default)] last:border-b-0"
              data-testid={`user-row-${u.id}`}
            >
              <div className="flex items-center gap-2 flex-wrap">
                <div className="flex-1 min-w-0">
                  <div className="font-mono text-[13px] truncate">
                    {u.email}
                    {isSelf && (
                      <span className="ml-2 text-[10px] px-1.5 py-0.5 bg-[var(--accent-soft)] text-[var(--accent)] uppercase tracking-wider">
                        vous
                      </span>
                    )}
                  </div>
                  <div className="text-[10px] font-mono text-[var(--text-muted)] mt-0.5">
                    créé le {new Date(u.created_at).toLocaleDateString("fr-FR")}
                  </div>
                </div>
                <button
                  onClick={() => { setEditingId(editing ? null : u.id); setEditPw(""); }}
                  data-testid={`user-toggle-pw-${u.id}`}
                  className="min-h-[36px] px-2.5 border border-[var(--border-default)] hover:border-black text-[10px] uppercase tracking-widest font-mono flex items-center gap-1"
                  title="Changer le mot de passe"
                >
                  <KeyRound className="w-3 h-3" strokeWidth={1.75} />
                  Mot de passe
                </button>
                {!isSelf && (
                  <button
                    onClick={() => remove(u)}
                    disabled={busyId === u.id}
                    data-testid={`user-delete-${u.id}`}
                    className="min-h-[36px] min-w-[36px] flex items-center justify-center border border-[var(--border-default)] hover:border-[var(--accent-strong)] hover:text-[var(--accent-strong)] disabled:opacity-50"
                    aria-label="Supprimer"
                    title="Supprimer l'utilisateur"
                  >
                    <Trash2 className="w-3.5 h-3.5" strokeWidth={1.75} />
                  </button>
                )}
              </div>
              {editing && (
                <div className="mt-2 flex items-center gap-2" data-testid={`user-pw-editor-${u.id}`}>
                  <input
                    type="password"
                    minLength={8}
                    placeholder="nouveau mot de passe (8+)"
                    value={editPw}
                    onChange={(e) => setEditPw(e.target.value)}
                    data-testid={`user-pw-input-${u.id}`}
                    className="flex-1 min-h-[40px] border border-[var(--border-default)] px-3 font-mono text-sm rounded-none focus:outline-none focus:border-black"
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); savePassword(u); } }}
                    autoFocus
                  />
                  <button
                    onClick={() => savePassword(u)}
                    disabled={busyId === u.id || editPw.length < 8}
                    data-testid={`user-pw-save-${u.id}`}
                    className="min-h-[40px] px-3 bg-black text-white text-[10px] uppercase tracking-widest font-mono hover:bg-[#222] disabled:opacity-50"
                  >
                    Enregistrer
                  </button>
                  <button
                    onClick={() => { setEditingId(null); setEditPw(""); }}
                    data-testid={`user-pw-cancel-${u.id}`}
                    className="min-h-[40px] min-w-[40px] flex items-center justify-center border border-[var(--border-default)] hover:border-black"
                    aria-label="Annuler"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

